<script setup lang="ts">
import { UModalConfirm } from "#components";
import { EVENT_STATUS, EVENT_TYPE } from "~/constants";
import { SEGMENT_TRACK_EVENTS } from "~/constants/segment";
import useEventSelect from "~/hooks/events/use-event-select";
import useAsync from "~/hooks/use-async";
import ClipAPI from "~/services/clip";
import EventsAPI from "~/services/events";
import useEventStore from "~/stores/event";
import useUserStore from "~/stores/user";
import getErrorLocalePathMessage from "~/utils/locale";

export interface EventInfoProps {
  item: IModelEvent;
}
const props = defineProps<EventInfoProps>();

const modal = useModal();
const router = useRouter();
const toaster = useToast();
const { t, te } = useI18n();
const appConfig = useRuntimeConfig().public;
const userStore = useUserStore();
const eventStore = useEventStore();
const segment = useSegment();

const isClippingModeEnabled = appConfig.APP_CUSTOMER.web_sdk.enable_clipping;
const isDownloadClipAllowed = appConfig.APP_CUSTOMER.web_sdk.enabled_downloading_clip;
const isTaggingModeEnabled = appConfig.APP_CUSTOMER.web_sdk.enable_tagging_mode;

const isLoggedIn = computed(() => Boolean(userStore.user));
const isEventStartingSoon = computed(() => eventStore.isEventStartingSoon);
const isClip = computed(() => props.item.eventType === EVENT_TYPE.USER_CLIP);
const isHighlight = computed(() => props.item.eventType === EVENT_TYPE.USER_HIGHLIGHT || props.item.eventType === EVENT_TYPE.HIGHLIGHT);
const isNotUpcoming = computed(() => props.item.eventStatus !== EVENT_STATUS.UPCOMING);
const hasPlayerError = computed(() => eventStore.playerState?.error !== null);
const isExternal = computed(() => eventStore.data?.isExternal);
const canDownloadClip = computed(() => isClip.value && isLoggedIn.value && isClippingModeEnabled && isDownloadClipAllowed);
const canCreateClip = computed(() => {
  return isClippingModeEnabled
    && isLoggedIn.value
    && isNotUpcoming.value
    && !isClip.value
    && !isHighlight.value
    && props.item.isEventAvailableForUser
    && props.item.isEventAvailableInUserRegion;
});
const canCreateTag = computed(() => {
  return isTaggingModeEnabled
    && isLoggedIn.value
    && isNotUpcoming.value
    && !isClip.value
    && !isHighlight.value
    && props.item.isEventAvailableForUser
    && props.item.isEventAvailableInUserRegion
    && !isExternal.value;
});

const onError = (error: Error) => {
  const { message } = getErrorLocalePathMessage(error.message, { t, te });
  return toaster.error(message);
};

const onClickCreateClip = () => {
  eventStore.clips.isCreatingClip = true;
  eventStore.tags.isCreatingTag = false;
  segment.track(SEGMENT_TRACK_EVENTS.CLIPPING_PROCESS_STARTED, { event_id: props.item.id });
};
const onClickUpdateClip = () => {
  eventStore.clips.isEditingClip = true;
};
const onClickCreateTag = () => {
  eventStore.tags.isCreatingTag = true;
  eventStore.clips.isCreatingClip = false;
};

const onClickClipDelete = () => {
  modal.open(UModalConfirm, {
    heading: t("labels.modal_labels.delete_clip"),
    icon: "clip",
    confirmLabel: t("button.delete"),
    variant: "delete",
    confirmFn: () => {
      return EventsAPI.userMedia.deleteUserClip(props.item.id)
        .then(() => toaster.success(t("pages.events.tabs.event_videos.clip_deleted_successfully_text")))
        .then(() => router.push({ path: `/events/${props.item.eventId}` }))
        .catch(onError);
    },
  });
};

const { isSelected, loading: loadingSelectEvent, toggleEventSelect: onClickSelect } = useEventSelect(props.item.id);
const { loading: loadingDownloadClip, run: downloadClip } = useAsync(ClipAPI.download);
</script>

<template>
  <BaseButton
    v-if="canDownloadClip"
    :disabled="hasPlayerError || isEventStartingSoon || loadingDownloadClip || !props.item?.source?.clip"
    class="text-link !no-underline"
    type="secondary"
    name="download-clip"
    link
    @click="downloadClip(props.item)"
  >
    <div class="flex items-center gap-2 hover:bg-[var(--base-category-chip-bgColor)] px-2 py-1 rounded-[var(--base-border-radius)]">
      <IconDownload class="inline-block" fill="currentColor" :width="16" :height="16" />
      <span class="text-[var(--base-secondary-text-color)]">{{ t('button.download') }}</span>
    </div>
  </BaseButton>
  <BaseButton
    v-if="canCreateClip"
    :disabled="hasPlayerError || isEventStartingSoon"
    class="text-link !no-underline"
    type="secondary"
    name="create-clip"
    link
    @click="onClickCreateClip"
  >
    <div class="flex items-center gap-2 hover:bg-[var(--base-category-chip-bgColor)] px-2 py-1 rounded-[var(--base-border-radius)]">
      <IconClip class="inline-block" fill="currentColor" :active="true" :width="16" :height="16" />
      <span class="text-[var(--base-secondary-text-color)]">{{ t('labels.clip', 1) }}</span>
    </div>
  </BaseButton>
  <BaseButton
    v-if="canCreateTag"
    :disabled="hasPlayerError || isEventStartingSoon"
    class="text-link !no-underline"
    type="secondary"
    name="create-tag"
    link
    @click="onClickCreateTag"
  >
    <div class="flex items-center gap-2 hover:bg-[var(--base-category-chip-bgColor)] px-2 py-1 rounded-[var(--base-border-radius)]">
      <IconTag class="inline-block" fill="currentColor" :active="isSelected" :width="16" :height="16" />
      <span class="text-[var(--base-secondary-text-color)]">{{ t('labels.tag', 1) }}</span>
    </div>
  </BaseButton>
  <EventInfoShare :event="props.item" :can-share-on-schedule="isNotUpcoming" />
  <BaseButton
    v-if="isLoggedIn"
    :disabled="loadingSelectEvent"
    class="text-link !no-underline"
    type="secondary"
    name="event-add-bookmark"
    link
    @click="onClickSelect"
  >
    <div class="flex items-center gap-2 hover:bg-[var(--base-category-chip-bgColor)] px-2 py-1 rounded-[var(--base-border-radius)]">
      <IconBookmark class="inline-block" fill="currentColor" :active="isSelected" :width="16" :height="16" />
      <span class="text-[var(--base-secondary-text-color)]">{{ isSelected ? t('labels.bookmark_remove') : t('labels.bookmark_add') }}</span>
    </div>
  </BaseButton>

  <UMenu v-if="isClip">
    <template #trigger="{ show }">
      <div class="hover:bg-[var(--base-category-chip-bgColor)] px-2 pt-1 rounded-[var(--base-border-radius)]">
        <BaseButton
          name="menu"
          title="Menu"
          type="icon"
          @click.stop="show"
        >
          <IconAction />
        </BaseButton>
      </div>
    </template>
    <template #default>
      <UList class="dark:!bg-neutral-dark-600">
        <UListItem size="md">
          <BaseButton
            class="w-full text-link row-center"
            type="icon"
            name="delete clip button"
            @click="onClickClipDelete"
          >
            <div class="flex items-center !text-red-500 gap-4 truncate">
              <IconDelete fill="currentColor" />
              {{ t('button.delete') }}
            </div>
          </BaseButton>
        </UListItem>

        <UDivider lighter />

        <UListItem size="md">
          <BaseButton
            class="w-full text-secondary text-link row-center"
            type="icon"
            name="rename clip button"
            @click="onClickUpdateClip"
          >
            <div class="flex items-center gap-4 truncate text-main">
              <IconEdit />
              {{ t('button.rename') }}
            </div>
          </BaseButton>
        </UListItem>
      </UList>
    </template>
  </UMenu>
</template>
